
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useSupabaseAuth } from '@/hooks/auth/supabaseAuth';
import { Loader2, User, Shield, Settings, Bug } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import AdminAuthDebug from '@/components/debug/AdminAuthDebug';
import PasswordChangeSection from '@/components/profile/PasswordChangeSection';
import { DataExportPanel } from '@/components/privacy/DataExportPanel';

const Profile = () => {
  const { user, profile, userRole, updateProfile, isLoading, refreshSession } = useSupabaseAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showAdminDebug, setShowAdminDebug] = useState(false);

  useEffect(() => {
    if (profile?.name) {
      setName(profile.name);
    }
  }, [profile]);

  const handleUpdateProfile = async () => {
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setIsUpdating(true);
    try {
      await updateProfile({ name: name.trim() });
      toast.success('Profile updated successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRefreshProfile = async () => {
    try {
      await refreshSession();
      toast.success('Profile refreshed');
    } catch (error) {
      console.error('Error refreshing profile:', error);
      toast.error('Failed to refresh profile');
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle loading and missing profile cases more gracefully
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (isLoading || !user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <h1 className="text-2xl font-bold">Loading Profile...</h1>
          </div>
          <p className="text-muted-foreground">Please wait while we load your profile data.</p>
        </div>
      </div>
    );
  }

  // Show basic profile even if some data is missing
  const displayProfile = profile || {
    id: user?.id || '',
    name: user?.email?.split('@')[0] || 'User',
    email: user?.email || 'unknown@email.com',
    is_approved: false,
    created_at: new Date().toISOString()
  };

  // Use actual profile data if available, otherwise fallback to user email
  const displayName = profile?.name || displayProfile.name;
  const displayEmail = profile?.email || user?.email || displayProfile.email;

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive' as const;
      case 'staff':
        return 'default' as const;
      default:
        return 'secondary' as const;
    }
  };

  const isAdminUser = userRole === 'admin';

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Profile</h1>
          <p className="text-muted-foreground">Manage your account settings and preferences</p>
        </div>
        
        {isAdminUser && (
          <Button
            variant="outline"
            onClick={() => setShowAdminDebug(!showAdminDebug)}
            className="flex items-center gap-2"
          >
            <Bug className="h-4 w-4" />
            {showAdminDebug ? 'Hide' : 'Show'} Admin Debug
          </Button>
        )}
      </div>

      {/* Admin Debug Panel */}
      {showAdminDebug && isAdminUser && (
        <AdminAuthDebug />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={displayProfile.avatar_url} />
                  <AvatarFallback className="text-lg">
                    {displayProfile.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold">{displayName}</h3>
                  <p className="text-muted-foreground">{displayEmail}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant={getRoleBadgeVariant(userRole || 'student')}>
                      <Shield className="h-3 w-3 mr-1" />
                      {userRole?.charAt(0).toUpperCase() + userRole?.slice(1) || 'Student'}
                    </Badge>
                    {displayProfile.is_approved ? (
                      <Badge variant="default">Approved</Badge>
                    ) : (
                      <Badge variant="secondary">Pending Approval</Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  {isEditing ? (
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter your full name"
                    />
                  ) : (
                    <div className="p-2 border rounded-md bg-muted/50">
                      {displayName || 'Not set'}
                    </div>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <div className="p-2 border rounded-md bg-muted/50">
                    {displayEmail}
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                {isEditing ? (
                  <>
                    <Button 
                      onClick={handleUpdateProfile} 
                      disabled={isUpdating}
                    >
                      {isUpdating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Save Changes
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setIsEditing(false)}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => setIsEditing(true)}>
                      <Settings className="h-4 w-4 mr-2" />
                      Edit Profile
                    </Button>
                    <Button variant="outline" onClick={handleRefreshProfile}>
                      Refresh Data
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Password Change Section */}
          <PasswordChangeSection />

          {/* GDPR Data Export */}
          <DataExportPanel />
        </div>

        {/* Account Status */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Account Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Account Type</Label>
                <div className="mt-1">
                  <Badge variant={getRoleBadgeVariant(userRole || 'student')} className="text-sm">
                    {userRole?.charAt(0).toUpperCase() + userRole?.slice(1) || 'Student'}
                  </Badge>
                </div>
              </div>

              <div>
                <Label>Approval Status</Label>
                <div className="mt-1">
                  {displayProfile.is_approved ? (
                    <Badge variant="default" className="text-sm">✅ Approved</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-sm">⏳ Pending</Badge>
                  )}
                </div>
              </div>

              <div>
                <Label>Member Since</Label>
                <div className="text-sm text-muted-foreground mt-1">
                  {displayProfile.created_at ? new Date(displayProfile.created_at).toLocaleDateString() : 'Unknown'}
                </div>
              </div>

              <div>
                <Label>User ID</Label>
                <div className="text-xs text-muted-foreground mt-1 font-mono">
                  {user.id}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
